import React, { useEffect, useMemo, useState } from 'react';
import { Search, Plus } from 'lucide-react';
import Skeleton from '../common/Skeleton';
import {
    ProcedureTemplate,
    Agency,
    OwnershipScope,
    procedureTemplateService,
} from '../../services/auditService';
import FormControl from '../common/FormControl';
import Button from '../common/Button';
import Pagination from '../common/Pagination';
import NoDataFound from '../common/NoDataFound';
import StatusSwitch from '../common/StatusSwitch';
import { NOT_AUTHORISED_MSG } from '../../contexts/PermissionContext';
import ApprovalDot, { WorkflowState } from '../common/ApprovalDot';

interface AuditProceduresProps {
    agencies: Agency[];
    /** Bump from parent after create/update/inactivate/reactivate to force a refetch. */
    refreshNonce?: number;
    onAdd: () => void;
    onConfigure: (proc: ProcedureTemplate) => void;
    /** Opens the status-dot workflow modal (submit/approve/reject/reopen). */
    onWorkflow?: (id: number) => void;
    /** Entry-point RBAC gate (ADT_PROCEDURES, view+modify) — disables the
     * toolbar Create button so an unauthorized user is stopped before opening
     * the modal (dead-end), not only at the API. */
    canModify?: boolean;
    /** True while permission grants are loading — disables Create (no tooltip)
     * to avoid a flash of an enabled button. */
    permLoading?: boolean;
}

// Must cover every procedure_type the backend accepts (see the CHECK on
// ADT_procedure_templates.procedure_type), otherwise those rows render with a
// blank TYPE badge and cannot be filtered from the "All types" dropdown.
// Labels mirror ProcedurePickerModal so the same type reads the same everywhere.
const TYPE_LABELS: Record<string, string> = {
    Checklist: 'Checklist',
    Questionnaire: 'Questionnaire',
    Form: 'Form',
    Rating: 'Rating System',
    Confirmation: 'Balance Confirmation',
    PhysicalCheck: 'Physical Inspection',
    Walkthrough: 'Process Walkthrough (legacy)',
    WalkthroughDesign: 'Walkthrough — Test of Design',
    WalkthroughCompliance: 'Walkthrough — Test of Compliance',
    RiskAssessment: 'Risk Assessment',
    ControlTest: 'Control Test',
};

const SCOPE_BADGE: Record<OwnershipScope, { label: string; cls: string }> = {
    'GRC-Common':     { label: 'GRC-Common',     cls: 'status-badge sm info' },
    'Agency-Private': { label: 'Agency-Private', cls: 'status-badge sm warning' },
};

const AuditProcedures: React.FC<AuditProceduresProps> = ({
    agencies,
    refreshNonce = 0,
    onAdd,
    onConfigure,
    onWorkflow,
    canModify = true,
    permLoading = false,
}) => {
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [typeFilter, setTypeFilter] = useState<string>('ALL');
    const [scopeFilter, setScopeFilter] = useState<string>('ALL');
    const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('active');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const [items, setItems] = useState<ProcedureTemplate[]>([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loading, setLoading] = useState(true);

    const agencyById = useMemo(() => {
        const m: Record<number, string> = {};
        agencies.forEach(a => { m[a.id] = a.audit_agency_name; });
        return m;
    }, [agencies]);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
        return () => clearTimeout(t);
    }, [search]);

    useEffect(() => { setPage(1); }, [debouncedSearch, typeFilter, scopeFilter, statusFilter, pageSize]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        procedureTemplateService.listPaged({
            page,
            limit: pageSize,
            search: debouncedSearch || undefined,
            type:   typeFilter  === 'ALL' ? undefined : typeFilter,
            scope:  scopeFilter === 'ALL' ? undefined : scopeFilter,
            status: statusFilter === 'all' ? 'ALL'
                  : statusFilter === 'active' ? 'ACTIVE' : 'INACTIVE',
        })
            .then(res => {
                if (cancelled) return;
                setItems(res.items);
                setTotalRecords(res.total);
            })
            .catch(() => {
                if (cancelled) return;
                setItems([]);
                setTotalRecords(0);
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [page, pageSize, debouncedSearch, typeFilter, scopeFilter, statusFilter, refreshNonce]);

    // The status filter is applied server-side (see listPaged), so the page is
    // already correct and meta.total counts the whole filtered set. Deriving the
    // total from the current page instead would cap the list at one page.
    const paginatedRows = items;
    const displayTotalRecords = totalRecords;

    return (
        <div className="netbody-card flex flex-col h-full">
            <div className="netbody-header">
                {/* Status — iOS-style on/off switch (on = active) */}
                <StatusSwitch
                    value={statusFilter === 'inactive' ? 'INACTIVE' : 'ACTIVE'}
                    onChange={next => setStatusFilter(next === 'INACTIVE' ? 'inactive' : 'active')}
                    noun="procedures"
                    testId="SEL_audit_procedures_status_filter"
                />
                <div className="flex items-center gap-2 ml-auto">
                    <FormControl.SearchSelect
                        value={scopeFilter}
                        onChange={v => setScopeFilter(String(v ?? 'ALL'))}
                        className="filter-status"
                        noSearchIcon
                        data-testid="SEL_audit_procedures_scope_filter"
                        options={[
                            { value: 'ALL', label: 'All scopes' },
                            { value: 'GRC-Common', label: 'GRC-Common' },
                            { value: 'Agency-Private', label: 'Agency-Private' },
                        ]}
                    />
                    <FormControl.SearchSelect
                        value={typeFilter}
                        onChange={v => setTypeFilter(String(v ?? 'ALL'))}
                        className="filter-status"
                        noSearchIcon
                        data-testid="SEL_audit_procedures_type_filter"
                        options={[
                            { value: 'ALL', label: 'All types' },
                            ...Object.entries(TYPE_LABELS).map(([val, label]) => ({ value: val, label })),
                        ]}
                    />
                    <FormControl.Input
                        type="text"
                        placeholder="Search procedures…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        leadingIcon={<Search size={14} />}
                        className="filter-search"
                        data-testid="INP_audit_procedures_search"
                    />
                    <Button
                        variant="fill"
                        icon={Plus}
                        onClick={onAdd}
                        disabled={permLoading || !canModify}
                        title={!canModify ? NOT_AUTHORISED_MSG : undefined}
                        data-testid="BTN_audit_procedures_create"
                    >Create</Button>
                </div>
            </div>

            <div className="netbody-content">
                <div className="scroll-container flex-1 overflow-hidden">
                    <div className="table-list">
                        <div className="table-list-scroll">
                            <table className="table-list-table">
                                <thead>
                                    <tr>
                                        <th className="table-list-th table-list-cell--dot" aria-label="Workflow status"></th>
                                        <th className="table-list-th table-list-cell--sno">S.No</th>
                                        <th className="table-list-th">Code</th>
                                        <th className="table-list-th">Procedure Name</th>
                                        <th className="table-list-th">Type</th>
                                        <th className="table-list-th">Scope</th>
                                        <th className="table-list-th">Owner Agency</th>
                                    </tr>
                                </thead>
                                <tbody aria-busy={loading} aria-live="polite">
                                    {loading && <Skeleton.TableRows cols={['3xs', '3xs', 'xs', 'md', 'pill', 'pill', 'sm']} />}
                                    {!loading && paginatedRows.length === 0 && (
                                        <tr>
                                            <td colSpan={7} className="table-list-cell">
                                                <NoDataFound
                                                    title={debouncedSearch || typeFilter !== 'ALL' || scopeFilter !== 'ALL' ? 'No Matching Procedures' : 'No Procedures Found'}
                                                    message={debouncedSearch || typeFilter !== 'ALL' || scopeFilter !== 'ALL' ? 'No procedures match your search.' : 'Click "Create" to add one.'}
                                                />
                                            </td>
                                        </tr>
                                    )}
                                    {!loading && paginatedRows.map((proc, idx) => {
                                        const isActive = (proc.is_active ?? 1) === 1;
                                        const scope = (proc.ownership_scope || 'GRC-Common') as OwnershipScope;
                                        const scopeBadge = SCOPE_BADGE[scope] ?? SCOPE_BADGE['GRC-Common'];
                                        return (
                                            <tr
                                                key={proc.id}
                                                className={`table-list-row${!isActive ? ' opacity-60' : ''}`}
                                                onClick={() => { if (permLoading || !canModify) return; onConfigure(proc); }}
                                                title={!canModify ? NOT_AUTHORISED_MSG : 'Click to configure'}
                                                aria-disabled={permLoading || !canModify}
                                                style={permLoading || !canModify ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
                                                data-testid={`BTN_audit_procedures_row_${proc.id}_open`}
                                            >
                                                <td
                                                    className="table-list-cell table-list-cell--dot"
                                                    onClick={(e) => { e.stopPropagation(); onWorkflow && onWorkflow(proc.id); }}
                                                    title="Workflow — view timeline & act"
                                                    data-testid={`BTN_audit_procedures_row_${proc.id}_workflow`}
                                                >
                                                    <ApprovalDot
                                                        state={(proc.workflow_state || 'draft') as WorkflowState}
                                                        onClick={() => onWorkflow && onWorkflow(proc.id)}
                                                        title="Workflow — view timeline & act"
                                                    />
                                                </td>
                                                <td className="table-list-cell table-list-cell--sno">{(page - 1) * pageSize + idx + 1}</td>
                                                <td className="table-list-cell">
                                                    {proc.code
                                                        ? <span className="cell-code">{proc.code}</span>
                                                        : <span className="text-muted text-xs">—</span>}
                                                </td>
                                                <td className="table-list-cell">
                                                    <span className="table-list-cell-title">{proc.procedure_name}</span>
                                                    {proc.procedure_description && (
                                                        <span className="table-list-cell-sub">{proc.procedure_description}</span>
                                                    )}
                                                </td>
                                                <td className="table-list-cell">
                                                    <span className="status-badge sm info">
                                                        {TYPE_LABELS[proc.procedure_type] || proc.procedure_type}
                                                    </span>
                                                </td>
                                                <td className="table-list-cell">
                                                    <span className={scopeBadge.cls}>
                                                        {scopeBadge.label}
                                                    </span>
                                                </td>
                                                <td className="table-list-cell">
                                                    {proc.owner_agency_id
                                                        ? (agencyById[proc.owner_agency_id] || `Agency #${proc.owner_agency_id}`)
                                                        : <span className="italic text-slate-400">—</span>}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        <Pagination
                            page={page}
                            pageSize={pageSize}
                            totalRecords={displayTotalRecords}
                            onPageChange={setPage}
                            onPageSizeChange={setPageSize}
                            recordLabel="procedures"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AuditProcedures;
